/**
 * One past day of the Calendar, folded into the figures an operator
 * reads at a glance: passes, searches, and the decisions and pushes
 * that matter. Pure, so the cell and the popover say the same thing.
 *
 * `ran` is one entry of `GET /schedule/executions/by-day`:
 * `{ runs, created, decisions: { event_type: n }, push: { state: n, … } }`.
 */

//: Order is reading order. `tone` is a key of the DS Repricing tone map
//: (`@components/tones.js`), the one the cells and the popover paint with.
//: `query` is what the decisions popup asks Activity for when the
//: chip is clicked (server filter names); a figure without one is
//: not clickable — refused calls are calls, not decisions.
const FIGURES = [
	{ key: 'changes',    label: 'price changes', tone: 'ok',      query: { event_type: 'applied' },
	  of: d => (d.applied || 0) },
	{ key: 'tethered',   label: 'tethered',      tone: 'info',    query: { event_type: 'tethered' },
	  of: d => (d.tethered || 0) },
	{ key: 'capped',     label: 'capped',        tone: 'warn',    query: { event_type: 'capped' },
	  of: d => (d.capped || 0) + (d.blocked_by_limit || 0) },
	{ key: 'quarantined', label: 'quarantined',  tone: 'accent',  query: { event_type: 'quarantined' },
	  of: d => (d.quarantined || 0) },
	{ key: 'errors',     label: 'PMS errors',    tone: 'err',     query: { event_type: 'pms_error' },
	  of: d => (d.pms_error || 0) + (d.tethered_pms_error || 0) },
	{ key: 'unchanged',  label: 'unchanged',     tone: 'neutral', query: { event_type: 'no_price_change' },
	  of: d => (d.no_price_change || 0) },
	{ key: 'no_strategy', label: 'no strategy',  tone: 'neutral', query: { event_type: 'no_strategy' },
	  of: d => (d.no_strategy || 0) },
]

const PUSHES = [
	{ key: 'not_sent',   label: 'not sent',   tone: 'info',   query: { decided_not_sent: '1' },
	  of: p => (p.pending || 0) + (p.failed || 0) + (p.flushing || 0) },
	{ key: 'superseded', label: 'superseded', tone: 'neutral', query: { decided_not_sent: '1' },
	  of: p => (p.superseded || 0) },
	{ key: 'refused',    label: 'calls refused', tone: 'err', query: null,
	  of: p => (p.calls_error || 0) },
]

/** `[{ key, label, n, tone }]` — only the figures that are not zero. */
export function figuresOf(ran) {
	if (!ran) return []
	const d = ran.decisions || {}
	const p = ran.push || {}
	return [
		...FIGURES.map(f => ({ key: f.key, label: f.label, tone: f.tone, query: f.query, n: f.of(d) })),
		...PUSHES.map(f => ({ key: f.key, label: f.label, tone: f.tone, query: f.query, n: f.of(p) })),
	].filter(f => f.n > 0)
}

/** "3 passes · 74 searches" — or null when nothing ran. */
export function headlineOf(ran) {
	if (!ran) return null
	const runs = ran.runs || 0
	const created = ran.created || 0
	const decided = Object.values(ran.decisions || {}).reduce((s, n) => s + (n || 0), 0)
	if (!runs && !created && !decided) return null
	const parts = []
	if (runs) parts.push(`${runs} pass${runs === 1 ? '' : 'es'}`)
	if (created) parts.push(`${created} search${created === 1 ? '' : 'es'}`)
	if (!runs && decided) parts.push(`${decided} decision${decided === 1 ? '' : 's'}`)
	return parts.join(' · ')
}

export const fmt = (n) => new Intl.NumberFormat('en-GB').format(n || 0)
